import React, { useState, useEffect } from 'react';
import { supabase } from '../../data/supabaseClient';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../hooks/ToastContext';
import { CustomButton10, CustomButton10Co, CustomButton10Ca, ModernDropdown } from '../../Templates';
import Spinner from '../../common/Spinner';
import { FaClock, FaMapMarkerAlt, FaCheckCircle, FaTruck, FaCalendarAlt } from 'react-icons/fa';

const DeliveryMeetingAgreement = ({ 
  delivery, 
  currentUser, 
  otherUser, 
  userRole, 
  packageData,
  onUpdate, 
  addSystemMessage, 
  createNotification 
}) => {
  const { t } = useTranslation('common');
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [proposedDays, setProposedDays] = useState(0);
  const [proposedHours, setProposedHours] = useState(6);
  const [showTimeSelector, setShowTimeSelector] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);

  // Determine user roles for delivery
  const isPackageSender = currentUser?.id === delivery?.sender_id;
  const isCarrier = currentUser?.id === delivery?.carrier_id;

  // Calculate delivery time countdown if agreed
  useEffect(() => {
    if (!delivery?.delivery_scheduled_time) return;

    const updateTimer = () => {
      const scheduledTime = new Date(delivery.delivery_scheduled_time);
      const remaining = Math.max(0, scheduledTime.getTime() - Date.now());
      setTimeRemaining(remaining);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [delivery?.delivery_scheduled_time]);

  // Check if delivery time has been reached and auto-transition
  useEffect(() => {
    if (!delivery?.delivery_scheduled_time || delivery?.status !== 'delivery_meeting_agreed') return;

    const checkDeliveryTime = () => {
      const scheduledTime = new Date(delivery.delivery_scheduled_time);
      const now = new Date();
      
      // If delivery time has been reached, transition to arrival phase
      if (now >= scheduledTime) {
        handleAutoTransitionToArrival();
      }
    };

    // Check immediately
    checkDeliveryTime();
    
    // Check every 30 seconds
    const interval = setInterval(checkDeliveryTime, 30000);
    return () => clearInterval(interval);
  }, [delivery?.delivery_scheduled_time, delivery?.status]);

  // Auto-transition to delivery arrival phase
  const handleAutoTransitionToArrival = async () => {
    try {
      const { data: updatedDelivery, error: updateError } = await supabase
        .from('package_deliveries')
        .update({
          status: 'delivery_arrival_phase',
          updated_at: new Date().toISOString()
        })
        .eq('id', delivery.id)
        .select()
        .single();

      if (updateError) throw updateError;

      // Call onUpdate with updated data
      if (onUpdate && updatedDelivery) {
        onUpdate(updatedDelivery);
      }

      // Add system message
      await addSystemMessage(
        t('package_actions.delivery_time_reached_system')
      );

      // Notify both users
      await createNotification(
        delivery.sender_id,
        'delivery_time_reached',
        t('package_actions.delivery_time_reached_notification'),
        delivery.id
      );
      
      await createNotification(
        delivery.carrier_id,
        'delivery_time_reached',
        t('package_actions.delivery_time_reached_notification'),
        delivery.id
      );

    } catch (error) {
      console.error('Error auto-transitioning to delivery arrival:', error);
    }
  };

  // Format countdown time
  const formatTimeRemaining = (ms) => {
    if (ms <= 0) return t('package_actions.delivery_time_reached');
    
    const days = Math.floor(ms / (24 * 60 * 60 * 1000));
    const hours = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
    
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  // Calculate proposed delivery time
  const calculateDeliveryTime = (days, hours) => {
    const pickupTime = delivery?.package_pickup_confirmed_at ? 
      new Date(delivery.package_pickup_confirmed_at) : 
      new Date();
    
    const deliveryTime = new Date(pickupTime);
    deliveryTime.setDate(deliveryTime.getDate() + parseInt(days));
    deliveryTime.setHours(deliveryTime.getHours() + parseInt(hours));
    
    return deliveryTime;
  };

  // Handle proposing delivery time
  const handleProposeDeliveryTime = async () => {
    if (proposedDays < 0 || proposedHours < 0) {
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: t('package_actions.invalid_time_values')
      });
      return;
    }

    if (proposedDays === 0 && proposedHours < 1) {
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: t('package_actions.minimum_delivery_time')
      });
      return;
    }

    setLoading(true);
    
    try {
      const deliveryTime = calculateDeliveryTime(proposedDays, proposedHours);
      
      const proposalData = {
        proposed_by: currentUser.id,
        proposed_days: parseInt(proposedDays),
        proposed_hours: parseInt(proposedHours),
        proposed_delivery_time: deliveryTime.toISOString(),
        proposed_at: new Date().toISOString(),
        status: 'pending'
      };

      // Get current proposals and add new one
      const currentProposals = delivery.delivery_time_proposals || [];
      const updatedProposals = [...currentProposals, proposalData];

      // Update delivery with new proposal
      const { data: updatedDelivery, error: updateError } = await supabase
        .from('package_deliveries')
        .update({
          delivery_time_proposals: updatedProposals,
          status: 'delivery_meeting_agreement',
          updated_at: new Date().toISOString()
        })
        .eq('id', delivery.id)
        .select()
        .single();

      if (updateError) throw updateError;

      // Call onUpdate with updated data
      if (onUpdate && updatedDelivery) {
        onUpdate(updatedDelivery);
      }

      // Get other user ID for notification
      const otherUserId = isCarrier ? delivery.sender_id : delivery.carrier_id;

      // Format time for display
      const timeString = deliveryTime.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });

      // Create notification
      await createNotification(
        otherUserId,
        'delivery_time_proposed',
        t('package_actions.delivery_time_proposed_notification', {
          days: proposedDays,
          hours: proposedHours,
          time: timeString
        }),
        delivery.id
      );

      // Add system message
      await addSystemMessage(
        t('package_actions.delivery_time_proposed_system', {
          user: otherUser?.username || userRole,
          days: proposedDays,
          hours: proposedHours
        })
      );

      addToast({
        type: 'success',
        title: t('package_actions.delivery_proposed'),
        description: t('package_actions.waiting_for_agreement')
      });

      setShowTimeSelector(false);

    } catch (error) {
      console.error('Error proposing delivery time:', error);
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: t('package_actions.error_proposing_delivery')
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle responding to delivery proposal
  const handleDeliveryResponse = async (accept, proposal) => {
    setLoading(true);
    
    try {
      if (accept) {
        // Accept the proposed delivery
        const { data: updatedDelivery, error: updateError } = await supabase
          .from('package_deliveries')
          .update({
            delivery_scheduled_days: proposal.proposed_days,
            delivery_scheduled_hours: proposal.proposed_hours,
            delivery_scheduled_time: proposal.proposed_delivery_time,
            delivery_meeting_agreed_at: new Date().toISOString(),
            delivery_timer_start_allowed_at: proposal.proposed_delivery_time,
            status: 'delivery_meeting_agreed',
            updated_at: new Date().toISOString()
          })
          .eq('id', delivery.id)
          .select()
          .single();

        if (updateError) throw updateError;

        // Call onUpdate with updated data
        if (onUpdate && updatedDelivery) {
          onUpdate(updatedDelivery);
        }

        // Notify the proposer
        await createNotification(
          proposal.proposed_by,
          'delivery_time_accepted',
          t('package_actions.delivery_time_accepted_notification'),
          delivery.id
        );

        // Add system message
        const agreedTime = new Date(proposal.proposed_delivery_time);
        const timeString = agreedTime.toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
        
        await addSystemMessage(
          t('package_actions.delivery_time_agreed_system', {
            days: proposal.proposed_days,
            hours: proposal.proposed_hours,
            time: timeString
          })
        );

        addToast({
          type: 'success',
          title: t('package_actions.delivery_time_agreed'),
          description: t('package_actions.delivery_scheduled')
        });

      } else {
        // Decline the proposal - remove it from proposals
        const updatedProposals = (delivery.delivery_time_proposals || [])
          .filter(p => p.proposed_at !== proposal.proposed_at);

        const { data: updatedDelivery, error: updateError } = await supabase
          .from('package_deliveries')
          .update({
            delivery_time_proposals: updatedProposals,
            // If no proposals left, go back to package_picked_up status
            status: updatedProposals.length === 0 ? 'package_picked_up' : 'delivery_meeting_agreement',
            updated_at: new Date().toISOString()
          })
          .eq('id', delivery.id)
          .select()
          .single();

        if (updateError) throw updateError;

        // Call onUpdate with updated data
        if (onUpdate && updatedDelivery) {
          onUpdate(updatedDelivery);
        }

        // Notify about decline
        await createNotification(
          proposal.proposed_by,
          'delivery_time_declined',
          t('package_actions.delivery_time_declined_notification'),
          delivery.id
        );

        // Add system message
        await addSystemMessage(
          t('package_actions.delivery_time_declined_system', { user: otherUser?.username || userRole })
        );

        addToast({
          type: 'info',
          title: t('package_actions.delivery_declined'),
          description: t('package_actions.propose_new_delivery')
        });
      }

    } catch (error) {
      console.error('Error responding to delivery proposal:', error);
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: t('package_actions.error_responding_delivery')
      });
    } finally {
      setLoading(false);
    }
  };

  // If delivery time is agreed and we're in countdown phase
  if (delivery?.status === 'delivery_meeting_agreed' && delivery?.delivery_scheduled_time) {
    const scheduledTime = new Date(delivery.delivery_scheduled_time);
    const isTimeReached = timeRemaining <= 0;
    
    return (
      <div className="PAC-meeting-section">
        <div className="PAC-meeting-title">
          <FaCheckCircle size={14} />
          {t('package_actions.delivery_scheduled')}
        </div>
        
        <div className="PAC-location-section">
          <div className="PAC-location-title">
            <FaCalendarAlt size={12} />
            {t('package_actions.delivery_details')}
          </div>
          <div className="PAC-location-address">
            <strong>{t('package_actions.scheduled_time')}:</strong> {scheduledTime.toLocaleString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            })}
          </div>
          <div className="PAC-location-address">
            <strong>{t('package_actions.time_format')}:</strong> {delivery.delivery_scheduled_days}d {delivery.delivery_scheduled_hours}h {t('package_actions.from_pickup')}
          </div>
        </div>
        
        <div className="PAC-timer-section">
          <div className={`PAC-delivery-countdown ${isTimeReached ? 'expired' : ''}`} 
               style={{ color: isTimeReached ? '#10B981' : 'var(--accent-color2)' }}>
            {formatTimeRemaining(timeRemaining)}
          </div>
          <div className="PAC-timer-status">
            {isTimeReached ? 
              t('package_actions.ready_for_delivery') : 
              t('package_actions.delivery_countdown')
            }
          </div>
        </div>
        
        {isTimeReached && (
          <div style={{ fontSize: '11px', textAlign: 'center', color: 'var(--accent-color)', marginTop: '8px' }}>
            {t('package_actions.delivery_time_instructions')}
          </div>
        )}
      </div>
    );
  }

  // Get active proposals
  const activeProposals = (delivery?.delivery_time_proposals || [])
    .filter(p => p.status === 'pending');
  const myProposals = activeProposals.filter(p => p.proposed_by === currentUser?.id);
  const otherProposals = activeProposals.filter(p => p.proposed_by !== currentUser?.id);

  // If there are proposals to respond to
  if (otherProposals.length > 0) {
    const latestProposal = otherProposals[otherProposals.length - 1];
    const proposedTime = new Date(latestProposal.proposed_delivery_time);

    return (
      <div className="PAC-meeting-section">
        <div className="PAC-meeting-title">
          <FaTruck size={14} />
          {t('package_actions.delivery_proposal')}
        </div>
        
        <div className="PAC-location-section">
          <div className="PAC-location-title">
            <FaClock size={12} />
            {t('package_actions.proposed_delivery_timing')}
          </div>
          <div className="PAC-location-address">
            <strong>{t('package_actions.timing')}:</strong> {latestProposal.proposed_days}d {latestProposal.proposed_hours}h {t('package_actions.from_pickup')}
          </div>
          <div className="PAC-location-address">
            <strong>{t('package_actions.delivery_time')}:</strong> {proposedTime.toLocaleString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            })}
          </div>
        </div>
        
        <div className="PAC-action-buttons-v2">
          <CustomButton10Co
            onClick={() => handleDeliveryResponse(true, latestProposal)}
            disabled={loading}
          >
            {loading ? <Spinner size="small" showText={false} thickness={2} /> : t('buttons.accept')}
          </CustomButton10Co>
          
          <CustomButton10Ca
            onClick={() => handleDeliveryResponse(false, latestProposal)}
            disabled={loading}
          >
            {loading ? <Spinner size="small" showText={false} thickness={2} /> : t('buttons.decline')}
          </CustomButton10Ca>
        </div>
      </div>
    );
  }

  // If showing my pending proposals
  if (myProposals.length > 0) {
    const myLatestProposal = myProposals[myProposals.length - 1];
    const proposedTime = new Date(myLatestProposal.proposed_delivery_time);

    return (
      <div className="PAC-meeting-section">
        <div className="PAC-meeting-title">
          <FaClock size={14} />
          {t('package_actions.waiting_for_delivery_response')}
        </div>
        
        <div className="PAC-location-section">
          <div className="PAC-location-title">
            <FaClock size={12} />
            {t('package_actions.your_proposal')}
          </div>
          <div className="PAC-location-address">
            <strong>{t('package_actions.timing')}:</strong> {myLatestProposal.proposed_days}d {myLatestProposal.proposed_hours}h {t('package_actions.from_pickup')}
          </div>
          <div className="PAC-location-address">
            <strong>{t('package_actions.delivery_time')}:</strong> {proposedTime.toLocaleString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            })}
          </div>
        </div>
        
        <div style={{ fontSize: '11px', textAlign: 'center', color: 'var(--main-color)', opacity: 0.8 }}>
          {t('package_actions.waiting_for_other_response', { 
            role: otherUser?.username || (isCarrier ? t('package_actions.sender') : t('package_actions.carrier')) 
          })}
        </div>
      </div>
    );
  }

  // No active proposals - show delivery planning
  return (
    <div className="PAC-meeting-section">
      <div className="PAC-meeting-title">
        <FaTruck size={14} />
        {t('package_actions.plan_delivery')}
      </div>
      
      <div style={{ fontSize: '11px', textAlign: 'center', marginBottom: '8px' }}>
        {t('package_actions.both_can_propose_time')}
      </div>
      
      {!showTimeSelector ? (
        <CustomButton10
          onClick={() => setShowTimeSelector(true)}
          label={t('package_actions.propose_delivery_time')}
          disabled={loading}
        />
      ) : (
        <div>
          <div className="PAC-timing-selector">
            <div className="PAC-timing-row">
              <ModernDropdown
                label={t('package_actions.days')}
                value={proposedDays.toString()}
                options={['0', '1', '2', '3', '4', '5', '6', '7']}
                onSelect={(option) => setProposedDays(parseInt(option))}
                icon={<FaCalendarAlt size={12} />}
                className="PAC-timing-dropdown"
              />
            </div>
            
            <div className="PAC-timing-row">
              <ModernDropdown
                label={t('package_actions.hours')}
                value={proposedHours.toString()}
                options={Array.from({length: 24}, (_, i) => (i + 1).toString())}
                onSelect={(option) => setProposedHours(parseInt(option))}
                icon={<FaClock size={12} />}
                className="PAC-timing-dropdown"
              />
            </div>
          </div>
          
          <div className="PAC-timing-preview">
            <strong>{t('package_actions.delivery_will_be')}:</strong><br />
            {calculateDeliveryTime(proposedDays, proposedHours).toLocaleString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            })}
          </div>
          
          <div className="PAC-action-buttons-v2" style={{ marginTop: '8px' }}>
            <CustomButton10Co
              onClick={handleProposeDeliveryTime}
              disabled={loading}
            >
              {loading ? <Spinner size="small" showText={false} thickness={2} /> : t('package_actions.propose_timing')}
            </CustomButton10Co>
            
            <CustomButton10Ca
              onClick={() => setShowTimeSelector(false)}
              disabled={loading}
            >
              {t('buttons.cancel')}
            </CustomButton10Ca>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryMeetingAgreement;
